import { useEffect, useState } from 'react'

import { droppedVoiceNamesWarning } from './droppedVoiceNamesWarning'

import styles from './RenameDialog.module.scss'

import type { GtRoom, GtSpeaker } from '@/api/internal'
import Button from '@/components/Button'
import DialogWrapper from '@/components/Dialog'
import {
  normalizeSpeakerName,
  planRoomJoin,
  roomDepartureHasNameCollision,
  SPEAKER_NAME_MAX_CODEPOINTS,
} from '@/pages/app/AttributionViewer/annotate/gtModel'

export interface RenameDialogProps {
  isOpen: boolean
  speaker: GtSpeaker | null
  speakers: readonly GtSpeaker[]
  rooms: readonly GtRoom[]
  close: () => void
  onRename: (speakerId: string, toName: string) => void
  onNameVoice: (speakerId: string, name: string | null) => void
  onJoinRoom: (speakerId: string, label: string) => void
  onLeaveRoom: (speakerId: string) => void
  onDelete: (speakerId: string) => void
}

export const RenameDialog = ({
  isOpen,
  speaker,
  speakers,
  rooms,
  close,
  onRename,
  onNameVoice,
  onJoinRoom,
  onLeaveRoom,
  onDelete,
}: RenameDialogProps) => {
  const isRoomVoice = speaker?.room_id != null
  const fromName = (isRoomVoice ? speaker?.voice_name : speaker?.name) ?? ''
  const [toName, setToName] = useState(fromName)
  const [roomId, setRoomId] = useState('')

  useEffect(() => {
    if (isOpen) {
      setToName(fromName)
      setRoomId('')
    }
  }, [isOpen, fromName])

  const trimmed = toName.trim()
  const collision =
    !isRoomVoice && speaker
      ? (speakers.find(
          (sp) => sp.id !== speaker.id && normalizeSpeakerName(sp.name) === normalizeSpeakerName(trimmed)
        ) ?? null)
      : null

  const voiceCollision =
    isRoomVoice && speaker && trimmed
      ? speakers.find(
          (sp) =>
            sp.id !== speaker.id &&
            sp.room_id === speaker.room_id &&
            sp.voice_name != null &&
            normalizeSpeakerName(sp.voice_name) === normalizeSpeakerName(trimmed)
        )
      : null

  const room = rooms.find((item) => item.id === speaker?.room_id)
  const ordinal =
    speaker && room
      ? speakers.filter((item) => item.room_id === room.id).findIndex((item) => item.id === speaker.id) + 1
      : 0
  const leavingName = speaker?.voice_name ?? speaker?.name ?? ''
  const leaveCollision = isRoomVoice && speaker && roomDepartureHasNameCollision(speakers, new Set([speaker.id]))
  const joinDroppedNames =
    !isRoomVoice && speaker && roomId ? planRoomJoin(speakers, new Set([speaker.id]), roomId).droppedNames : []

  return (
    <DialogWrapper
      isOpen={isOpen}
      title={
        isRoomVoice
          ? `Name voice ${ordinal} in ${room?.label ?? 'room'}`
          : fromName
            ? `Rename "${fromName}"`
            : 'Rename speaker'
      }
      close={close}
    >
      <div className={styles.body}>
        {isRoomVoice && (
          <p>
            Set the verified person behind this room voice. The source label “{speaker?.name}” is only a hint. The name
            will appear on turns and in the export with the room label.
          </p>
        )}
        <label className={styles.label} htmlFor="gt-rename-input">
          {isRoomVoice ? 'Person name' : 'New name'}
          <input
            id="gt-rename-input"
            className={styles.input}
            value={toName}
            maxLength={SPEAKER_NAME_MAX_CODEPOINTS}
            onChange={(event) => setToName(event.target.value)}
          />
        </label>
        {collision && (
          <p className={styles.warning}>
            {`A speaker named "${collision.name}" already exists. Renaming will merge "${fromName}" into "${collision.name}" — their turns and hotkey are combined and this speaker is removed.`}
          </p>
        )}
        {voiceCollision && (
          <p className={styles.warning}>
            Another voice in this room is already named “{voiceCollision.voice_name}”. Give each voice a distinct name.
          </p>
        )}
        {isRoomVoice && leaveCollision && (
          <p className={styles.warning}>
            {`Another speaker is already named "${leavingName}". Rename or clear this voice's name before removing it from the room.`}
          </p>
        )}
        {!isRoomVoice && speaker && rooms.length > 0 && (
          <label className={styles.label} htmlFor="gt-join-room">
            Add to an existing room
            <select
              id="gt-join-room"
              className={styles.input}
              value={roomId}
              onChange={(event) => setRoomId(event.target.value)}
            >
              <option value="">Select a room</option>
              {rooms.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
        )}
        {joinDroppedNames.length > 0 && <p className={styles.warning}>{droppedVoiceNamesWarning(joinDroppedNames)}</p>}
        <div className={styles.actions}>
          {speaker && (
            <Button
              title="Delete speaker"
              type="destructive"
              className={styles.delete}
              onClick={() => {
                close()
                onDelete(speaker.id)
              }}
            />
          )}
          <Button title="Cancel" type="link" onClick={close} />
          {isRoomVoice && (
            <Button
              title="Remove from room"
              type="link"
              isDisabled={!!leaveCollision}
              onClick={() => {
                if (speaker) onLeaveRoom(speaker.id)
                close()
              }}
            />
          )}
          {isRoomVoice && fromName && (
            <Button
              title="Clear name"
              type="link"
              onClick={() => {
                if (speaker) onNameVoice(speaker.id, null)
                close()
              }}
            />
          )}
          {!isRoomVoice && rooms.length > 0 && (
            <Button
              title="Join room"
              type="dark"
              isDisabled={!speaker || !roomId}
              onClick={() => {
                const room = rooms.find((item) => item.id === roomId)
                if (speaker && room) onJoinRoom(speaker.id, room.label)
                close()
              }}
            />
          )}
          <Button
            title={isRoomVoice ? 'Save name' : collision ? 'Merge' : 'Rename'}
            type="dark"
            isDisabled={!speaker || !trimmed || trimmed === fromName || !!voiceCollision}
            onClick={() => {
              if (!speaker) return
              if (isRoomVoice) onNameVoice(speaker.id, trimmed)
              else onRename(speaker.id, trimmed)
              close()
            }}
          />
        </div>
      </div>
    </DialogWrapper>
  )
}
